/**
 * visualStyled.ts — Styled-components locales de la tab "Visual & Logo".
 *
 * Qué hace: define los styled-components propios de BrandTabVisual.tsx (filas
 *   del catálogo de fonts, uploader, swatch remove button, toggle de
 *   forceBrandColors). Los styled compartidos siguen en brandShared.
 * Qué exporta: cada styled-component con `export const`.
 * Quién lo usa: components/brand/BrandTabVisual.tsx.
 */
import styled from 'styled-components';

// Fila por fuente del catálogo global: muestra el sample en vivo + meta + delete.
export const FontFileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.background};
  margin-bottom: 6px;
`;

export const FontFileSample = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FontFileMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const DeleteFontBtn = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textMuted};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  flex-shrink: 0;
  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

// Wrapper de cada swatch para anclar el botón × de borrado.
export const SwatchWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

// Botón × explícito por swatch: el ::after hover-only del ColorSwatch compartido
// no es alcanzable en touch, así que damos un control siempre clickeable. En
// punteros finos (mouse) queda discreto y aparece al hover; en touch siempre visible.
export const SwatchRemoveBtn = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.error};
  color: white;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${SwatchWrap}:hover & {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    opacity: 1;
  }

  /* En punteros gruesos (touch) no hay hover → el control debe estar siempre visible. */
  @media (hover: none) {
    opacity: 1;
  }
`;

// Grid del uploader: nombre + weight + botón. Colapsa a 1 columna en mobile.
export const UploadFontRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.6fr auto;
  gap: 8px;
  align-items: end;
  padding: 12px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.background};

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const WeightSelect = styled.select`
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

// Etiqueta del checkbox de forceBrandColors.
export const ToggleRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
`;

export const ToggleCheckbox = styled.input`
  margin-top: 3px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

export const SavedRow = styled.div`
  min-height: 18px;
  margin-top: 4px;
`;
